import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase-auth';
import { useAuth } from '../contexts/AuthContextLazy';

interface Feature {
  feature_key: string;
  display_name: string;
  category: string;
  configuration: any;
}

interface FeatureFlags {
  dashboard: boolean;
  profile: boolean;
  document_vault: boolean;
  document_categories: boolean;
  document_versioning: boolean;
  document_expiry: boolean;
  messaging: boolean;
  message_attachments: boolean;
  video_messages: boolean;
  portfolio_snapshot: boolean;
  goal_tracking: boolean;
  net_worth_tracking: boolean;
  missing_pieces: boolean;
  client_requests: boolean;
  request_tracking: boolean;
  mymeetings_sync: boolean;
  bank_aggregation: boolean;
  market_data: boolean;
}

export const useFeatureFlags = () => {
  const { user, userProfile } = useAuth();
  const [features, setFeatures] = useState<Feature[]>([]);
  const [featureFlags, setFeatureFlags] = useState<Partial<FeatureFlags>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch enabled features for the current user
  const fetchFeatures = useCallback(async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .rpc('get_enabled_features_for_user', { p_user_id: user.id });

      if (fetchError) throw fetchError;

      setFeatures(data || []);
      
      // Convert to feature flags object
      const flags: Partial<FeatureFlags> = {};
      (data || []).forEach((feature: Feature) => {
        flags[feature.feature_key as keyof FeatureFlags] = true;
      });
      setFeatureFlags(flags);
    } catch (err) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Error fetching feature flags:', err);
        }

      }
      setError(err instanceof Error ? err.message : 'Failed to fetch features');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Check if a specific feature is enabled
  const isFeatureEnabled = useCallback((featureKey: keyof FeatureFlags): boolean => {
    return featureFlags[featureKey] || false;
  }, [featureFlags]);

  // Get configuration for a specific feature
  const getFeatureConfig = useCallback((featureKey: string): any => {
    const feature = features.find(f => f.feature_key === featureKey);
    return feature?.configuration || null;
  }, [features]);

  // Get all features in a specific category
  const getFeaturesByCategory = useCallback((category: string): Feature[] => {
    return features.filter(f => f.category === category);
  }, [features]);

  // Toggle feature visibility for current user
  const toggleFeatureVisibility = useCallback(async (featureKey: string, hide: boolean) => {
    if (!user?.id) return;

    try {
      // Get current hidden features
      const { data: prefs } = await supabase
        .from('user_portal_preferences')
        .select('hidden_features')
        .eq('user_id', user.id)
        .single();

      let hiddenFeatures = prefs?.hidden_features || [];

      if (hide && !hiddenFeatures.includes(featureKey)) {
        hiddenFeatures = [...hiddenFeatures, featureKey];
      } else if (!hide) {
        hiddenFeatures = hiddenFeatures.filter((f: string) => f !== featureKey);
      }

      // Update preferences
      const { error: updateError } = await supabase
        .from('user_portal_preferences')
        .upsert({
          user_id: user.id,
          hidden_features: hiddenFeatures
        });

      if (updateError) throw updateError;

      // Refresh features
      await fetchFeatures();
    } catch (err) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Error toggling feature visibility:', err);
        }

      }
      throw err;
    }
  }, [user?.id, fetchFeatures]);

  // Log feature access for analytics
  const logFeatureAccess = useCallback(async (featureKey: string, action: string = 'view') => {
    if (!user?.id || !userProfile?.organization_id) return;

    try {
      await supabase
        .from('portal_feature_access_logs')
        .insert({
          user_id: user.id,
          organization_id: userProfile.organization_id,
          feature_key: featureKey,
          action
        });
    } catch (err) {
      // Don't throw - logging shouldn't break the app
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Error logging feature access:', err);
        }

      }
    }
  }, [user?.id, userProfile?.organization_id]);

  // Fetch features on mount and when user changes
  useEffect(() => {
    fetchFeatures();
  }, [fetchFeatures]);

  // Subscribe to real-time updates for organization settings
  useEffect(() => {
    if (!userProfile?.organization_id) return;

    const subscription = supabase
      .channel(`org-features:${userProfile.organization_id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'organization_portal_settings',
          filter: `organization_id=eq.${userProfile.organization_id}`
        },
        () => {
          // Refresh features when org settings change
          fetchFeatures();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [userProfile?.organization_id, fetchFeatures]);

  return {
    features,
    featureFlags,
    loading,
    error,
    isFeatureEnabled,
    getFeatureConfig,
    getFeaturesByCategory,
    toggleFeatureVisibility,
    logFeatureAccess,
    refetchFeatures: fetchFeatures
  };
};

// Hook for checking a single feature
export const useFeature = (featureKey: keyof FeatureFlags) => {
  const { isFeatureEnabled, logFeatureAccess } = useFeatureFlags();
  
  const isEnabled = isFeatureEnabled(featureKey);

  // Log access when component using this feature mounts
  useEffect(() => {
    if (isEnabled) {
      logFeatureAccess(featureKey);
    }
  }, [isEnabled, featureKey, logFeatureAccess]);

  return isEnabled;
};